import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getPortalAssignments,
  createUserAssignments,
} from "../../../services/api/apiClient";
import { Status } from "../../../services/api/types/assignments/assignment";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentFormLayout from "../components/AssignmentFormLayout";
import { useAssignmentForm } from "../hooks/useAssignmentForm";
import { SelectedUser } from "../types";
import { usePortal } from "../../../context/PortalsContext";
import { DataTable, ColumnDef } from "../../../components/DataTable/DataTable";
import { useToast } from "../../../context/ToastContext";
import { usePageMetadata } from "../../../context/PageMetadataContext";

interface AssignmentFormValues {
  group: string;
  deadlineDate: string;
  deadlineOffset: string;
  notify: boolean;
}

interface AddAssignmentProps {
  onClose?: () => void;
  onSuccess?: (selectedAssignments: string[]) => void;
  initialSelectedUsers?: SelectedUser[];
  initialSelectedAssignments?: string[];
  initialFormValues?: Partial<AssignmentFormValues>;
}

const AddAssignment: React.FC<AddAssignmentProps> = ({
  onClose,
  onSuccess,
  initialSelectedUsers = [],
  initialSelectedAssignments = [],
  initialFormValues = {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { portals } = usePortal();
  const selectedPortal = portals.selectedPortal;

  const usersFromState =
    (location.state as { selectedUsers?: SelectedUser[] })?.selectedUsers || [];

  const { formValues, handleFormChange, setFormValues } = useAssignmentForm({
    group: "",
    deadlineDate: "",
    deadlineOffset: "7",
    notify: true,
    setDeadlineToNull: false,
    ...initialFormValues,
  });

  const [selectedUsers] = useState<SelectedUser[]>(
    usersFromState.length > 0 ? usersFromState : initialSelectedUsers,
  );

  const [selectedAssignments, setSelectedAssignments] = useState<string[]>(
    initialSelectedAssignments,
  );
  const [selectedRowsMap, setSelectedRowsMap] = useState<Map<string, any>>(
    new Map(),
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [openDeadlinePicker, setOpenDeadlinePicker] = useState<boolean>(false);

  // Table state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState<string>("title");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const prevSearchTermRef = useRef(searchTerm);
  const { showToast } = useToast();

  useEffect(() => {
    if (Object.keys(initialFormValues).length > 0) {
      setFormValues((prev) => ({
        ...prev,
        ...initialFormValues,
      }));
    }
  }, [initialFormValues, setFormValues]);

  const fetchAssignments = useCallback(async () => {
    const portalId =
      localStorage.getItem("selectedPortal") ||
      (selectedPortal && selectedPortal.portalUid);
    if (!portalId) {
      showToast("Portal ID is missing", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await getPortalAssignments(portalId, {
        short: true,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        orderBy: orderBy || "title",
        sort: sortOrder as "asc" | "desc",
        status: Status.ACTIVE,
        search: searchTerm || undefined,
      });

      setRows(response.rows || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      showToast("Failed to load assignments", "error");
    } finally {
      setLoading(false);
    }
  }, [
    selectedPortal,
    page,
    rowsPerPage,
    orderBy,
    sortOrder,
    searchTerm,
    showToast,
  ]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    prevSearchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Helper to get row ID
  const getRowId = (row: any, index: number): string => {
    return String(
      row.portalAssignmentUid ||
        row.assignmentUid ||
        row.code ||
        row.title ||
        index,
    );
  };

  // Update selected rows map when rows change
  useEffect(() => {
    setSelectedRowsMap((prev) => {
      const updated = new Map(prev);
      rows.forEach((row) => {
        const id = getRowId(row, 0);
        if (selectedAssignments.includes(id)) {
          updated.set(id, row);
        }
      });
      return updated;
    });
  }, [rows, selectedAssignments]);

  // Get all selected rows (across all pages)
  const selectedRows = useMemo(() => {
    return Array.from(selectedRowsMap.values());
  }, [selectedRowsMap]);

  const handleSelectionChange = (newSelectedRows: any[]) => {
    const newSelectedIds = newSelectedRows.map((row) => getRowId(row, 0));
    const newSelectedRowsMap = new Map<string, any>();
    newSelectedRows.forEach((row) => {
      const id = getRowId(row, 0);
      newSelectedRowsMap.set(id, row);
    });
    setSelectedAssignments(newSelectedIds);
    setSelectedRowsMap(newSelectedRowsMap);
  };

  const isRowSelected = (row: any): boolean => {
    const id = getRowId(row, 0);
    return selectedAssignments.includes(id);
  };

  const handleSortChange = (newOrderBy: string, newOrder: "asc" | "desc") => {
    setOrderBy(newOrderBy);
    setSortOrder(newOrder);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleSearchChange = useCallback((value: string) => {
    const prev = prevSearchTermRef.current;
    prevSearchTermRef.current = value;
    setSearchTerm(value);
    if (prev !== value) {
      setPage(1);
    }
  }, []);

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      showToast(
        "Please select at least one user. You can select users from the Users page and navigate back.",
        "error",
      );
      return;
    }

    if (selectedAssignments.length === 0) {
      showToast("Please select at least one assignment", "error");
      return;
    }

    try {
      setIsSubmitting(true);

      const portalGroupUid = formValues.group
        ? String(formValues.group).split(":").pop()
        : undefined;

      const requestBody: any = {
        users: selectedUsers.map((user) => user.UserPortalUid),
        assignments: selectedAssignments,
        portalGroupUid,
        initialNotification: formValues.notify,
      };

      const hasValidDeadlineDate =
        formValues.deadlineDate && formValues.deadlineDate.trim() !== "";
      if (hasValidDeadlineDate) {
        requestBody.dueDate = new Date(formValues.deadlineDate);
      }

      // Handle plusDays independently - ensure it's not negative
      if (
        formValues.deadlineOffset &&
        formValues.deadlineOffset.trim() !== ""
      ) {
        const plusDays = parseInt(formValues.deadlineOffset, 10);
        if (!isNaN(plusDays) && plusDays >= 0) {
          requestBody.plusDays = plusDays;
        }
      }

      const response = await createUserAssignments(
        // @ts-ignore
        selectedPortal?.portalUid || "",
        requestBody,
      );

      const status = (response?.status ?? "").toUpperCase();

      if (response.status === "WARNING") {
        showToast(response.body, "warning", null);
      } else {
        showToast("Assignments created successfully!", "success");
      }

      if (status !== "WARNING") {
        onSuccess?.(selectedAssignments);
      }

      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      }
    } catch (err) {
      showToast("Failed to save assignment", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnDef<any>[] = useMemo(
    () => [
      {
        id: "title",
        label: "Title",
        sortable: true,
        render: (row) => <Typography>{row.title}</Typography>,
      },
      {
        id: "code",
        label: "Code",
        sortable: true,
        render: (row) => <Typography>{row.code || "-"}</Typography>,
      },
      {
        id: "packageType",
        label: "Type",
        sortable: true,
        render: (row) => <Typography>{row.packageType || "-"}</Typography>,
      },
    ],
    [],
  );

  const assignmentsTable = (
    <Box>
      <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
        Select assignments
      </Typography>
      <Typography variant="body2" sx={{ fontSize: 14, mb: 3 }}>
        Choose which assignments to add from the list below. At least one
        assignment is required
      </Typography>

      <DataTable
        data={rows}
        columns={columns}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        sort={{ orderBy, order: sortOrder }}
        onSortChange={handleSortChange}
        search={searchTerm}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search by title, code or type"
        isLoading={loading}
        enableSelection={true}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        isRowSelected={isRowSelected}
        getRowId={getRowId}
      />
    </Box>
  );

  const { portalUid } = useParams<{ portalUid: string }>();

  usePageMetadata(
    {
      title: "Add New Assignment",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Manage assignments",
          path: `/portal-configuration/${portalUid}/users/manage-assignments`,
        },
        {
          label: "Add assignment",
        },
      ],
    },
    [portalUid],
  );

  return (
    <Box>
      {onClose && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <AssignmentFormLayout
        mode="create"
        extraContent={assignmentsTable}
        selectedItems={selectedUsers}
        itemType="users"
        formValues={formValues}
        onFormValuesChange={handleFormChange}
        onSubmit={handleSubmit}
        openDeadlinePicker={openDeadlinePicker}
        onOpenDeadlinePickerChange={() => setOpenDeadlinePicker(true)}
        onCloseDeadlinePickerChange={() => setOpenDeadlinePicker(false)}
        submitButtonText={isSubmitting ? "Saving..." : "Add assignment"}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

export default AddAssignment;
